import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import { weekStartOf } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { FilterChip } from "@/components/app/lists/filter-chip";
import { PageHeader } from "@/components/app/shell/page-header";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useFacilityToday } from "@/hooks/use-facility-today";
import { useIsMobile } from "@/hooks/use-mobile";
import { useWeekRota } from "@/hooks/use-week-rota";
import { useFacility } from "@/lib/facility/facility";
import { Clock, Pencil, Plus, Printer, Settings2 } from "@/lib/icons";
import { useCan } from "@/lib/policy";
import { readState } from "@/lib/query/read-state";
import { Board } from "./-components/board";
import { buildBoard, countGaps, weekOf, WHOLE_BUILDING } from "./-components/board-model";
import { ClearWeekButton } from "./-components/clear-week-button";
import { CopyWeekButton } from "./-components/copy-week-button";
import { DayList } from "./-components/day-list";
import { DepartmentIcon } from "./-components/department-style";
import { HoursDialog } from "./-components/hours-dialog";
import { EVERYBODY, PersonPicker } from "./-components/person-picker";
import { PersonWeekDialog } from "./-components/person-week-dialog";
import { PresetCards } from "./-components/preset-cards";
import { PrintDialog } from "./-components/print-dialog";
import { ShiftDialog } from "./-components/shift-dialog";
import type { ShiftTarget } from "@/routes/_authenticated/_workforce/schedule/-components/shift-dialog-shared";
import { WeekNav } from "@/components/app/lists/week-nav";
import { AskOliviaButton } from "@/components/olivia/ask-olivia-button";

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const ALL = "__all__";

export const Route = createFileRoute("/_authenticated/_workforce/schedule/")({
  validateSearch: (search: Record<string, unknown>): { week?: string; department?: string } => ({
    week: typeof search.week === "string" && DAY.test(search.week) ? search.week : undefined,
    department:
      typeof search.department === "string" && search.department ? search.department : undefined,
  }),
  component: SchedulePage,
});

/** The schedule: a wall rota on a screen. Shifts down the side, the week across the top, and the
 * people in between. Somebody who has never seen it can read who is on tonight, and a manager fills
 * a gap with two presses: the gap, then the person. */
function SchedulePage() {
  const { facilityId } = useFacility();
  const today = useFacilityToday(facilityId);
  const canManage = useCan("schedule:manage");
  const canSeePay = useCan("hr:manage", facilityId === "all" ? null : facilityId);
  const isMobile = useIsMobile();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const thisWeek = weekStartOf(today);
  const weekStart = search.week ? weekStartOf(search.week) : thisWeek;
  const days = useMemo(() => weekOf(weekStart), [weekStart]);
  const [person, setPerson] = useState(EVERYBODY);
  const [target, setTarget] = useState<ShiftTarget | null>(null);
  const [mobileDay, setMobileDay] = useState<string | null>(null);
  const [printing, setPrinting] = useState(false);
  const [editingWeek, setEditingWeek] = useState(false);
  const [showingHours, setShowingHours] = useState(false);

  const setWeek = (week: string) =>
    void navigate({
      search: (previous) => ({ ...previous, week: week === thisWeek ? undefined : week }),
    });
  const setDepartment = (department: string | undefined) =>
    void navigate({ search: (previous) => ({ ...previous, department }) });

  const buildingChosen = facilityId !== "all";
  const { patterns, rota, staff } = useWeekRota(facilityId, weekStart);

  const everything = useMemo(
    () => buildBoard(patterns.data ?? [], rota.data ?? []),
    [patterns.data, rota.data],
  );
  const departments = everything.map((section) => section.department);
  const chosenDepartment = search.department;
  const sections = useMemo(() => {
    const byDepartment = chosenDepartment
      ? everything.filter((section) => (section.department ?? WHOLE_BUILDING) === chosenDepartment)
      : everything;
    if (person === EVERYBODY) return byDepartment;
    return byDepartment.map((section) => ({
      ...section,
      rows: section.rows.map((row) => ({
        ...row,
        cells: new Map(
          [...row.cells].map(([day, list]) => [day, list.filter((s) => s.userId === person)]),
        ),
      })),
    }));
  }, [everything, chosenDepartment, person]);

  const shiftsThisWeek = (rota.data ?? []).filter((shift) => shift.status !== "cancelled").length;
  const clearing = chosenDepartment === WHOLE_BUILDING ? null : chosenDepartment;
  const stillScheduled = (rota.data ?? []).filter(
    (shift) =>
      shift.status === "scheduled" &&
      (clearing === undefined || (shift.department ?? null) === clearing),
  ).length;
  const chosenStaff = (staff.data ?? []).find((row) => row.id === person);
  const chosenPerson = chosenStaff
    ? { id: chosenStaff.id, name: chosenStaff.name ?? "Unnamed" }
    : null;
  const personShifts = (rota.data ?? []).filter(
    (shift) => shift.userId === person && shift.status !== "cancelled",
  );
  const gaps = countGaps(sections, days);
  const read = readState(
    {
      data: patterns.data && rota.data ? true : undefined,
      isError: patterns.isError || rota.isError,
    },
    (patterns.data?.length ?? 0) + (rota.data?.length ?? 0),
  );
  const visibleDays = isMobile
    ? [days.includes(mobileDay ?? "") ? mobileDay! : days.includes(today) ? today : days[0]!]
    : days;

  const meta =
    read !== "ready"
      ? "Who is on which shift, day by day."
      : gaps === 0
        ? `${shiftsThisWeek} ${shiftsThisWeek === 1 ? "shift" : "shifts"} this week, every shift has somebody`
        : `${shiftsThisWeek} ${shiftsThisWeek === 1 ? "shift" : "shifts"} this week, ${gaps} still ${gaps === 1 ? "needs" : "need"} somebody`;

  return (
    <>
      <PageHeader
        title="Schedule"
        meta={meta}
        actions={
          <>
            <AskOliviaButton
              label="Check this week with Olivia"
              question={`Is everyone on the rota for the week of ${format(parseISO(weekStart), "d MMM yyyy")} trained and cleared for their shifts?`}
            />
            <Button onClick={() => setShowingHours(true)} size="sm" variant="outline">
              <Clock className="size-4" />
              Hours{canSeePay ? " and cost" : ""}
            </Button>
            {canManage && buildingChosen ? (
              <>
                <Button onClick={() => setPrinting(true)} size="sm" variant="outline">
                  <Printer className="size-4" />
                  Print
                </Button>
                <CopyWeekButton
                  department={chosenDepartment}
                  facilityId={facilityId}
                  weekStart={weekStart}
                />
                <ClearWeekButton
                  department={clearing}
                  facilityId={facilityId}
                  scheduled={stillScheduled}
                  weekStart={weekStart}
                />
                <Button asChild size="sm" variant="outline">
                  <Link to="/schedule/patterns">
                    <Settings2 className="size-4" />
                    Set up shifts
                  </Link>
                </Button>
              </>
            ) : null}
          </>
        }
      />

      <div className="my-4 flex flex-wrap items-center justify-between gap-3">
        <WeekNav onChange={setWeek} thisWeek={thisWeek} weekStart={weekStart} />
        <div className="flex flex-wrap items-center gap-2">
          {chosenPerson && canManage ? (
            <Button onClick={() => setEditingWeek(true)} size="sm" variant="outline">
              <Pencil className="size-4" />
              Edit {chosenPerson.name}'s week
            </Button>
          ) : null}
          <PersonPicker onChange={setPerson} staff={staff.data ?? []} value={person} />
        </div>
      </div>

      {read === "ready" && (departments.length > 0 || canManage) ? (
        <fieldset className="mb-3 flex flex-wrap items-center gap-2">
          <legend className="sr-only">Department</legend>
          <FilterChip onClick={() => setDepartment(undefined)} selected={!chosenDepartment}>
            Everyone
          </FilterChip>
          {departments.map((department) => {
            const label = department ?? WHOLE_BUILDING;
            return (
              <FilterChip
                key={label}
                onClick={() => setDepartment(chosenDepartment === label ? undefined : label)}
                selected={chosenDepartment === label}
              >
                <DepartmentIcon className="size-3.5" department={department} />
                {label}
              </FilterChip>
            );
          })}
          {chosenDepartment ? (
            <Button onClick={() => setDepartment(undefined)} size="sm" variant="ghost">
              Clear
            </Button>
          ) : null}
          {canManage && buildingChosen ? (
            <Button asChild size="sm" variant="ghost">
              <Link to="/schedule/patterns">
                <Plus className="size-4" />
                Add a department
              </Link>
            </Button>
          ) : null}
        </fieldset>
      ) : null}

      {isMobile && read === "ready" ? (
        <ToggleGroup
          aria-label="Day"
          className="mb-3 w-full"
          onValueChange={(next) => next && setMobileDay(next)}
          type="single"
          value={visibleDays[0] ?? ALL}
          variant="outline"
        >
          {days.map((day) => (
            <ToggleGroupItem className="h-11 flex-1 px-1 text-xs" key={day} value={day}>
              {format(parseISO(day), "EEE d")}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      ) : null}

      {read === "loading" ? (
        <Skeleton className="h-96 w-full rounded-xl" />
      ) : read === "failed" ? (
        <EmptyState
          title="Could not load the schedule"
          description="Nothing here means nothing was read, not that nobody is on."
          action={
            <Button
              disabled={rota.isFetching || patterns.isFetching}
              onClick={() => {
                void rota.refetch();
                void patterns.refetch();
              }}
            >
              Try again
            </Button>
          }
        />
      ) : read === "empty" ? (
        !buildingChosen ? (
          <EmptyState
            title="Choose a building"
            description="Each building runs its own shifts. Pick one in the switcher at the top."
          />
        ) : canManage ? (
          <div className="space-y-4">
            <EmptyState
              title="Set up this building's shifts"
              description="Pick the week that looks like yours. The schedule is ready to fill in straight after."
            />
            <PresetCards departments={[]} facilityId={facilityId} patterns={[]} headingLevel={2} />
          </div>
        ) : (
          <EmptyState
            title="The shifts have not been set up yet"
            description="A manager sets up the building's shifts first. Then the week shows who is on."
          />
        )
      ) : isMobile ? (
        <DayList
          canManage={canManage && buildingChosen}
          day={visibleDays[0]!}
          onAdd={(row, day) => setTarget({ row, day, shift: null })}
          onOpen={(shift) => setTarget({ row: null, day: shift.date, shift })}
          sections={sections}
          today={today}
        />
      ) : (
        <Board
          canManage={canManage && buildingChosen}
          days={visibleDays}
          onAdd={(row, day) => setTarget({ row, day, shift: null })}
          onOpen={(shift) => setTarget({ row: null, day: shift.date, shift })}
          sections={sections}
          today={today}
        />
      )}

      <HoursDialog
        canSeePay={canSeePay}
        department={chosenDepartment}
        facilityId={facilityId}
        onOpenChange={setShowingHours}
        open={showingHours}
        weekStart={weekStart}
      />
      <PrintDialog
        departments={departments}
        initialDepartment={chosenDepartment}
        key={`${chosenDepartment ?? ""}:${printing}`}
        onOpenChange={setPrinting}
        open={printing}
        week={weekStart}
      />
      <ShiftDialog
        dayShifts={(rota.data ?? []).filter(
          (shift) => shift.date === (target?.shift?.date ?? target?.day),
        )}
        facilityId={facilityId}
        onClose={() => setTarget(null)}
        patterns={patterns.data ?? []}
        staff={staff.data}
        target={target}
      />
      {chosenPerson ? (
        <PersonWeekDialog
          onClose={() => setEditingWeek(false)}
          open={editingWeek}
          person={chosenPerson}
          shifts={personShifts}
        />
      ) : null}
    </>
  );
}
